import { App } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import type { AppPort } from "./types";
import { dispatchAppUrl, setAppUrlFallback } from "./links";

/** 같은 URL 이 두 경로(iOS 콜드 스타트: 보존된 appUrlOpen + getLaunchUrl)로 연달아 오면 한 번만 넘긴다. */
const DUPLICATE_WINDOW_MS = 3000;
/** 이 프로세스에서 넘긴 URL 들. WebView 재로드(OTA 적용 등)는 같은 프로세스라 getLaunchUrl 이 같은 값을 다시 준다 → 다시 이동하지 않는다. 프로세스가 끝나면 비워진다. */
const HANDLED_URLS_KEY = "hamlak.openUrlsHandled";
const HANDLED_URLS_MAX = 20;

function handledUrls(): string[] {
  try {
    const v: unknown = JSON.parse(sessionStorage.getItem(HANDLED_URLS_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function markHandled(url: string): void {
  try {
    sessionStorage.setItem(HANDLED_URLS_KEY, JSON.stringify([...handledUrls().filter((u) => u !== url), url].slice(-HANDLED_URLS_MAX)));
  } catch {
    // 저장 실패는 재로드 때 한 번 더 이동할 뿐이다
  }
}

/** 네이티브: appStateChange(isActive=true) → 포그라운드 복귀. 리스너 핸들은 비동기로 오므로 해제도 그 뒤에 한다. */
export const nativeApp: AppPort = {
  onForeground(cb) {
    let removed = false;
    const handle = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive && !removed) cb();
    });
    return () => {
      removed = true;
      void handle.then((h) => h.remove()).catch(() => undefined);
    };
  },
  async getInfo() {
    try {
      const info = await App.getInfo();
      const build = Number.parseInt(info.build, 10);
      return { version: info.version, build: Number.isNaN(build) ? null : build };
    } catch {
      return { version: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0", build: null };
    }
  },
  // backButton 리스너가 하나라도 있으면 Capacitor 는 WebView goBack 기본 동작을 하지 않는다. 판단은 앱 셸이 한다(시트 닫기 → 뒤로 → 종료).
  onBackButton(cb) {
    let removed = false;
    const handle = App.addListener("backButton", () => {
      if (!removed) cb();
    });
    return () => {
      removed = true;
      void handle.then((h) => h.remove()).catch(() => undefined);
    };
  },
  async exit() {
    await App.exitApp();
  },
  // 첫 화면을 그렸으면 이 번들은 정상이다 → OTA notifyAppReady(appReadyTimeout 안에 안 부르면 플러그인이 이전 번들로 되돌린다, client-detail 1.24)
  async hideSplash() {
    await Promise.all([SplashScreen.hide().catch(() => undefined), CapacitorUpdater.notifyAppReady().catch(() => undefined)]);
  },
  // AC0-261: links.ts 디스패처의 폴백 = 라우트(auth)가 소비하지 않은 앱 스킴·https 앱 링크. 콜드 스타트는 getLaunchUrl 로 한 번 더 확인한다
  // (Android 는 launch 인텐트에 appUrlOpen 이 없고, iOS 는 보존된 appUrlOpen 과 같은 URL 이 올 수 있다).
  onOpenUrl(cb) {
    let last: { url: string; at: number } | null = null;
    const off = setAppUrlFallback((url) => {
      const href = url.href;
      const now = Date.now();
      if (last && last.url === href && now - last.at < DUPLICATE_WINDOW_MS) return true;
      last = { url: href, at: now };
      markHandled(href);
      cb(href);
      return true;
    });
    void App.getLaunchUrl()
      .then((r) => {
        const url = r?.url;
        if (!url || handledUrls().includes(url)) return;
        markHandled(url);
        dispatchAppUrl(url);
      })
      .catch(() => undefined);
    return off;
  },
};
